// src/sim/ai/awareness/accumulator.ts — P4 awareness accumulator, boundary emitter,
// and stability-check (D-09, D-10, D-11).
//
// Design invariants:
//   - Pure: all functions take value parameters and return new values; no mutation.
//   - Pitfall P4-H: decay is NOT called inside advanceAwareness. Caller gates on
//     perception cadence (10 Hz) and calls either advanceAwareness (target visible)
//     or decayAwareness (target not visible). Never called on every 30 Hz sim tick.
//   - Pitfall P4-I: stepStabilityCheck DECREMENTS (Math.max(0, prev-1)), never
//     resets to 0. This prevents a single below-threshold tick from wiping a
//     near-ready streak.
//   - Pitfall P4-J: emitBoundaryEvent uses strict prevLevel !== nextLevel comparison;
//     steady state at exactly a threshold emits no event.
//
// Source: .planning/phases/04-ai/04-04-PLAN.md Task 1; 04-CONTEXT.md D-09/D-10/D-11.

import {
  P4_AWARENESS_STABILITY_TICKS,
  type AwarenessLevel,
} from './constants';
// P7 POL-06 / D-12: awareness thresholds + rates read through the tuning cache
// (./constants are the fallback defaults). Stability ticks stays a code constant
// (not admin-editable).
import { getTuning } from '../../content/cache';

/**
 * Advance awareness scalar toward a visible target.
 *
 * delta = P4_AWARENESS_BASE_RATE × sightability × distFactor
 * where distFactor = max(0, 1 − distancePx / maxRangePx)
 *
 * Caller must gate this on perception tick cadence (10 Hz) — Pitfall P4-H.
 */
export function advanceAwareness(
  sightability: number,
  distancePx: number,
  maxRangePx: number,
  current: number,
): number {
  const distFactor = Math.max(0, 1 - distancePx / maxRangePx);
  const rate = getTuning().awarenessBaseRate * sightability * distFactor;
  return Math.min(1, current + rate);
}

/**
 * Decay awareness scalar when target is no longer visible.
 *
 * Decreases by P4_AWARENESS_DECAY_RATE, clamped at 0.
 * Caller must gate this on perception tick cadence (10 Hz) — Pitfall P4-H.
 */
export function decayAwareness(current: number): number {
  return Math.max(0, current - getTuning().awarenessDecayRate);
}

/**
 * Classify awareness scalar into AwarenessLevel.
 *
 * Boundaries use >= comparator so the exact threshold value maps to the higher
 * level (prevents Pitfall P4-J double-fire at exactly-threshold values when
 * combined with emitBoundaryEvent's prevLevel !== nextLevel strict-inequality check).
 */
export function classifyLevel(awareness: number): AwarenessLevel {
  const t = getTuning();
  if (awareness >= t.awarenessSpotted) return 'spotted';
  if (awareness >= t.awarenessAlerted) return 'alerted';
  if (awareness >= t.awarenessSuspicious) return 'suspicious';
  return 'unaware';
}

export interface ThresholdEvent {
  type: 'awareness-changed';
  entityId: string;
  level: AwarenessLevel;
  tick: number;
}

/**
 * Emit a boundary event if and only if the awareness level changed.
 *
 * Returns null when prevLevel === nextLevel (Pitfall P4-J: strict inequality).
 * Emits exactly ONE event per boundary crossing (Pattern 2).
 */
export function emitBoundaryEvent(
  prev: number,
  next: number,
  observerId: string,
  tick: number,
): ThresholdEvent | null {
  const prevLevel = classifyLevel(prev);
  const nextLevel = classifyLevel(next);
  if (prevLevel === nextLevel) return null;
  return { type: 'awareness-changed', entityId: observerId, level: nextLevel, tick };
}

/**
 * Advance the spotted-stability counter by one perception tick.
 *
 * When currentAwareness >= P4_AWARENESS_SPOTTED: increment counter.
 * When below threshold: DECREMENT by 1 (not reset to 0) — Pitfall P4-I.
 * Returns ready=true when counter reaches P4_AWARENESS_STABILITY_TICKS (D-10).
 */
export function stepStabilityCheck(
  prevStableTicks: number,
  currentAwareness: number,
): { nextStableTicks: number; ready: boolean } {
  const above = currentAwareness >= getTuning().awarenessSpotted;
  const next = above ? prevStableTicks + 1 : Math.max(0, prevStableTicks - 1);
  return { nextStableTicks: next, ready: next >= P4_AWARENESS_STABILITY_TICKS };
}
